import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocales } from 'src/locales';
import countriesJson from 'src/utils/countries.json';
import Iconify from 'src/components/iconify';
import CiCard from './CiCard';
import HoverShortcut from './HoverShortcut';
import { domainFavicon } from '../utils';

export interface TopCompetitorStat {
  domain: string;
  paidPresenceRate: number;
}

interface TopCompetitorsCardProps {
  title: string;
  dateRange: string;
  mainContent?: React.ReactNode;
  competitors?: TopCompetitorStat[];
  loading?: boolean;
  selectedCountry?: string | null;
}

const DEFAULT_COMPETITORS: TopCompetitorStat[] = [
 ];

function BrandFavicon({ brandDomain, size }: { brandDomain: string; size: number }) {
  
  useEffect(() => {
  }, [brandDomain]);
  
  if (!brandDomain) {
    return null;
  }
  
  const faviconUrl = domainFavicon(brandDomain);
  
  return (
    <Box
      component="img"
      alt={brandDomain}
      src={faviconUrl}
      onError={(e) => console.error('[BrandFavicon] Image failed to load for:', brandDomain, 'URL:', faviconUrl, 'Error:', e)}
      sx={{
        width: size,
        height: size,
        minWidth: size,
        minHeight: size,
        backgroundColor: '#fff',
        borderRadius: '50%',
        display: 'block',
        objectFit: 'contain',
        flexShrink: 0,
        border: '1px solid rgba(145, 158, 171, 0.2)',
        padding: '1px',
      }}
    />
  );
}

const hoverLabel = (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
    See Ads
    <Iconify icon="eva:diagonal-arrow-right-up-fill" width={14} />
  </Box>
);

function TopCompetitorBar({ value, domain, selectedCountry }: { value: number; domain: string; selectedCountry?: string | null }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const width = value * 5;
  const cleanDomain = domain
    ?.replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '')
    .trim() || domain;

  const handleShortcutClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const countryEntry = countriesJson?.find((c) => c.name === selectedCountry);
    const code = countryEntry?.code?.toUpperCase() || '';
    navigate(`/competitive-intelligence/ads?country=${encodeURIComponent(code)}&domain=${encodeURIComponent(domain)}`);
  };

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'relative',
        background: 'rgba(145, 158, 171, 0.20)',
        width: `${width}px`,
        minWidth: 'fit-content',
        height: '26px',
        borderRadius: '6px',
        paddingY: '4px',
        paddingX: '6px',
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" gap="14px" alignItems="center">
        <Typography fontSize="13px">{value}%</Typography>
        <BrandFavicon brandDomain={domain} size={20} />
        <Typography fontSize="13px" sx={{ minWidth: 0, flex: 1, whiteSpace: 'nowrap' }}>
          {cleanDomain}
        </Typography>
      </Stack>
      {selectedCountry && hovered && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            pl: 3,
            pr: 0.5,
            background: 'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(237,238,240,0.95) 30%)',
            backdropFilter: 'blur(2px)',
            borderRadius: '6px',
          }}
        >
          <HoverShortcut
            defaultContent=""
            hoverContent={hoverLabel}
            onClick={handleShortcutClick}
            forceHovered
            fontSize={13}
            sx={{ ml: 0 }}
          />
        </Box>
      )}
    </Box>
  );
}

export default function TopCompetitorsCard({ competitors, loading, selectedCountry, ...props }: TopCompetitorsCardProps) {
  const { t } = useLocales();
  const displayCompetitors = (competitors && competitors.length > 0 ? competitors : DEFAULT_COMPETITORS).slice(0, 3);
  
  const mainContent = displayCompetitors.length > 0 ? (
    <Stack direction="column" gap="10px" marginY="30px">
      {displayCompetitors.map((competitor) => (
        <Box key={competitor.domain}>
          <TopCompetitorBar value={Math.round(competitor.paidPresenceRate)} domain={competitor.domain} selectedCountry={selectedCountry} />
        </Box>
      ))}
    </Stack>
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '120px',
        marginY: '30px',
        color: 'text.secondary',
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
        {t('dashboard.global.no_data_available')}
      </Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '12px', mt: 1 }}>
        {t('dashboard.global.competitiveIntelligence.no_results_found_description')}
      </Typography>
    </Box>
  );

  return (
    <CiCard
      {...props}
      mainContent={mainContent}
      loading={loading}
      subTitle={t('dashboard.competitiveIntelligence.paidPresenceRate')}
    />
  );
}
